import {
  BulkName,
  TableDataType,
} from "@components/common/faro-table/faro-table-types";
import { GroupTypes } from "@custom-types/group-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { PointCloud } from "@custom-types/point-cloud-types";
import { Markup } from "@custom-types/project-markups-types";
import { SdbProject } from "@custom-types/project-types";
import { SdbTeam } from "@custom-types/teams-types";
import { createSelector } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { markupsSelector } from "@store/markups/markups-selector";
import { companyTeamsAsMemberSelector } from "@store/members/members-selector";
import { pointCloudsSelector } from "@store/point-clouds/point-clouds-selector";
import { selectedProjectSnapshotsSelector } from "@store/projects/projects-selector";
import { RootState } from "@store/store-helper";
import { StringEntityId } from "@store/store-types";
import { TableAdapter } from "@store/table/table-slice";
import {
  BulkActionResults,
  FetchingItem,
} from "@store/table/table-slice-helper";
import { TableType } from "@utils/track-event/track-event-list";
import { Snapshot } from "@custom-types/base-types";

/** Returns the bulk action table type */
export const tableTypeSelector: (state: RootState) => TableType | null =
  createSelector(
    (state: RootState) => state.table,
    (table) => {
      return table.tableType;
    }
  );

/** Returns the bulk action name */
export const bulkActionNameSelector: (state: RootState) => BulkName | null =
  createSelector(
    (state: RootState) => state.table,
    (table) => {
      return table.bulkActionName;
    }
  );

/** Returns the bulk action selected row Ids */
export const selectedRowIdsSelector: (state: RootState) => StringEntityId[] =
  createSelector(
    (state: RootState) => state.table,
    (table) => {
      return TableAdapter.getSelectors().selectIds(table);
    }
  );

/** Returns the bulk action results */
export const bulkActionResultsSelector: (
  state: RootState
) => BulkActionResults = createSelector(
  (state: RootState) => state.table,
  (table) => {
    return table.bulkActionResults;
  }
);

/** Returns all updating items */
export const updatingItemsSelector: (state: RootState) => FetchingItem[] =
  createSelector(
    (state: RootState) => state.table,
    (table) => {
      return TableAdapter.getSelectors().selectAll(table);
    }
  );

/** Returns the total number of selected items */
export const numberOfUpdatingItemsSelector: (state: RootState) => number =
  createSelector(
    (state: RootState) => state.table,
    (table) => {
      return TableAdapter.getSelectors().selectTotal(table);
    }
  );

// All the possible types to use in the selectedEntitiesSelector
export function selectedEntitiesSelector<T extends TableDataType>(
  bulkActionEntityType: T
): (
  state: RootState
) => T extends "members"
  ? SdbMemberTypes[]
  : T extends "projects"
  ? SdbProject[]
  : T extends "groups"
  ? GroupTypes[]
  : T extends "teams"
  ? SdbTeam[]
  : T extends "pointClouds"
  ? PointCloud[]
  : T extends "markups"
  ? Markup[]
  : T extends "mapProjects"
  ? SdbProject[]
  : T extends "snapshots"
  ? Snapshot[]
  : never;

/** Receive entity type and return all the selected items in table  */
export function selectedEntitiesSelector(
  bulkActionEntityType: TableDataType
): (
  state: RootState
) =>
  | SdbMemberTypes[]
  | SdbProject[]
  | GroupTypes[]
  | PointCloud[]
  | Markup[]
  | FaroCoreApiTypes.Team[]
  | Snapshot[] {
  switch (bulkActionEntityType) {
    case "projects":
      return selectedProjectEntitiesSelector;
    case "members":
      return selectedMemberEntitiesSelector;
    case "groups":
      return selectedGroupEntitiesSelector;
    case "teams":
      return selectedTeamEntitiesSelector;
    case "pointClouds":
      return selectedPointCloudEntitiesSelector;
    case "markups":
      return selectedMarkupEntitiesSelector;
    case "mapProjects":
      return selectedMapProjectEntitiesSelector;
    case "snapshots":
      return selectedSnapshotEntitiesSelector;

    default:
      throw new Error(`Unknown context type: ${bulkActionEntityType}`);
  }
}

// All the possible types to use in the selectedIdsSelector
export function selectedIdsSelector(
  bulkActionEntityType: "scans"
): (state: RootState) => StringEntityId[];

/** Receive entity type and return the IDs of all selected items in the table  */
export function selectedIdsSelector(
  bulkActionEntityType: TableDataType
): (state: RootState) => StringEntityId[] {
  switch (bulkActionEntityType) {
    case "scans":
      return selectedRowIdsSelector;

    default:
      throw new Error(`Unknown context type: ${bulkActionEntityType}`);
  }
}

/** Return all the member entities based on the updating items ID */
const selectedMemberEntitiesSelector: (state: RootState) => SdbMemberTypes[] =
  createSelector(
    selectedRowIdsSelector,
    (state: RootState) => state.members.entities,
    (selectedRowIds, memberEntities) => {
      return selectedRowIds.reduce<SdbMemberTypes[]>(
        (selectedEntities, selectedRow) => {
          const companyMember = memberEntities[selectedRow];

          if (companyMember) {
            selectedEntities.push(companyMember);
          }
          return selectedEntities;
        },
        []
      );
    }
  );

/** Return all the project entities based on the updating items ID */
const selectedProjectEntitiesSelector: (state: RootState) => SdbProject[] =
  createSelector(
    selectedRowIdsSelector,
    (state: RootState) => state.projects.entities,
    (selectedRowIds, projectEntities) => {
      return selectedRowIds
        .map((selectedRowId) => projectEntities[selectedRowId])
        .filter((project): project is SdbProject => !!project);
    }
  );

/** Return all the group entities based on the updating items ID */
const selectedGroupEntitiesSelector: (state: RootState) => GroupTypes[] =
  createSelector(
    selectedRowIdsSelector,
    (state: RootState) => state.groups.entities,
    (selectedRowIds, groupEntities) => {
      return selectedRowIds.reduce<GroupTypes[]>(
        (selectedEntities, selectedRow) => {
          const group = groupEntities[selectedRow];
          if (group) {
            selectedEntities.push(group);
          }
          return selectedEntities;
        },
        []
      );
    }
  );

/** Return all the team entities based on the updating items ID */
const selectedTeamEntitiesSelector: (state: RootState) => SdbTeam[] =
  createSelector(
    selectedRowIdsSelector,
    (state: RootState) => state.teams.entities,
    companyTeamsAsMemberSelector,
    (selectedRowIds, teamEntities, companyTeamsAsMember) => {
      return selectedRowIds.reduce<SdbTeam[]>(
        (selectedEntities, selectedRow) => {
          const team = teamEntities[selectedRow];

          if (team) {
            const teamAsMember = companyTeamsAsMember.find(
              (teamAsMember) => teamAsMember.identity === team.id
            );

            selectedEntities.push({
              ...team,
              role: teamAsMember?.role ?? "MEMBER",
            });
          }

          return selectedEntities;
        },
        []
      );
    }
  );

/** Return all the point cloud entities based on the updating items ID */
const selectedPointCloudEntitiesSelector: (state: RootState) => PointCloud[] =
  createSelector(
    selectedRowIdsSelector,
    pointCloudsSelector,
    (selectedRowIds, pointClouds) => {
      return selectedRowIds.reduce<PointCloud[]>(
        (selectedEntities, selectedRow) => {
          const pointCloud = pointClouds.find((pc) => pc.id === selectedRow);
          if (pointCloud) {
            selectedEntities.push(pointCloud);
          }
          return selectedEntities;
        },
        []
      );
    }
  );

/** Return all the markup entities based on the updating items ID */
const selectedMarkupEntitiesSelector: (state: RootState) => Markup[] =
  createSelector(
    selectedRowIdsSelector,
    markupsSelector,
    (selectedRowIds, markups) => {
      return selectedRowIds.reduce<Markup[]>(
        (selectedEntities, selectedRow) => {
          const markup = markups.find((m) => m.id === selectedRow);
          if (markup) {
            selectedEntities.push(markup);
          }
          return selectedEntities;
        },
        []
      );
    }
  );

/**
 * @returns all the map project entities based on the updating items ID
 */
const selectedMapProjectEntitiesSelector: (state: RootState) => SdbProject[] =
  createSelector(
    selectedRowIdsSelector,
    (state: RootState) => state.projectsMap.entities,
    (selectedRowIds, mapProjectEntities) => {
      return selectedRowIds
        .map((selectedRowId) => mapProjectEntities[selectedRowId])
        .filter((project): project is SdbProject => !!project);
    }
  );

/**
 * @returns all the snapshot entities based on the updating items ID
 */
const selectedSnapshotEntitiesSelector: (state: RootState) => Snapshot[] =
  createSelector(
    selectedRowIdsSelector,
    selectedProjectSnapshotsSelector,
    (selectedRowIds, snapshots) => {
      return selectedRowIds
        .map((selectedRowId) => snapshots.find((s) => s.id === selectedRowId))
        .filter((snapshot): snapshot is Snapshot => !!snapshot);
    }
  );

/** Returns the updating item based on the provided ID */
export function updatingItemByIdSelector(
  id: StringEntityId
): (state: RootState) => FetchingItem | null {
  return createSelector(
    (state: RootState) => state.table,
    (table) => {
      return TableAdapter.getSelectors().selectById(table, id) ?? null;
    }
  );
}

/** Returns whether any bulk action is in the provided status or not */
export function isUpdatingItemsInStatusStageSelector(
  status: FetchingItem["status"]
): (state: RootState) => boolean {
  return createSelector(updatingItemsSelector, (updatingItems) => {
    return updatingItems.some((item) => item.status === status);
  });
}

/** Returns whether any bulk action is still in progress or not */
export const isBulkActionProcessingSelector: (state: RootState) => boolean =
  createSelector(
    isUpdatingItemsInStatusStageSelector("updating"),
    isUpdatingItemsInStatusStageSelector("in-queue"),
    (isUpdating, isInQueue) => {
      return isUpdating || isInQueue;
    }
  );

/** Whether to show the success dialog or not */
export const shouldShowSuccessDialogSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state.table.bulkActionResults,
    (bulkActionResults) => {
      return (
        bulkActionResults.numberOfErrors === 0 &&
        bulkActionResults.numberOfSuccess > 0
      );
    }
  );

/** Whether to show the failed dialog or not */
export const shouldShowFailedDialogSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state.table.bulkActionResults,
    (bulkActionResults) => {
      return bulkActionResults.numberOfErrors > 0;
    }
  );

/** Whether any bulk action results are available or not */
export const isBulkActionsResultsAvailableSelector: (
  state: RootState
) => boolean = createSelector(
  (state: RootState) => state.table.bulkActionResults,
  (bulkActionResults) => {
    return (
      bulkActionResults.numberOfSuccess > 0 ||
      bulkActionResults.numberOfErrors > 0
    );
  }
);

/** Allowed bulk actions in a session */
export const allowedBulkButtonsInSessionSelector: (
  state: RootState
) => BulkName[] = createSelector(
  (state: RootState) => state.table,
  (table) => {
    return table.allowedBulkButtonsInSession;
  }
);
